import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import { getText } from 'i18n/intl';
import { keys } from 'utils/keys';
import type {
  OutlookConfiguration,
  OutlookConfigurationResponse,
} from '@timeedit/calendar-integration-api-contract';
import { TEDrawer } from '@timeedit/ui-components';
import { useForm } from '@timeedit/ui-components/antd/form/Form';
import MS365ConfigurationDetails, {
  intlPrefix,
} from './MS365ConfigurationDetails';

type Props = {
  config?: OutlookConfigurationResponse | null;
  secondConfig?: OutlookConfigurationResponse | null;
  onClose: (config: OutlookConfiguration) => void;
  onDelete: (config: OutlookConfiguration) => void;
  onDiscard: (config: OutlookConfiguration) => void;
  onSave: (config: OutlookConfiguration) => void;
};

const MS365ConfigurationDrawer = ({
  config,
  onClose,
  onDelete,
  onDiscard,
  onSave,
  secondConfig,
}: Props) => {
  const [form] = useForm<OutlookConfiguration>();
  const [secondForm] = useForm<OutlookConfiguration>();

  useEffect(() => {
    form.setFieldsValue(config);
    form.setFieldValue('clientSecret', undefined);
  }, [config]);

  useEffect(() => {
    secondForm.setFieldsValue(secondConfig);
    secondForm.setFieldValue('clientSecret', undefined);
  }, [secondConfig]);

  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isSecondSaveDisabled, setIsSecondSaveDisabled] = useState(true);

  const handleValuesChanged = useCallback(
    (
      values: OutlookConfiguration,
      setDisabled: Dispatch<SetStateAction<boolean>>,
      configuration: OutlookConfigurationResponse | null
    ) => {
      setDisabled(() =>
        keys(values).every((key) => values[key] === configuration?.[key])
      );
    },
    []
  );

  const handleFirstValuesChange = useCallback(
    (_: Partial<OutlookConfiguration>, values: OutlookConfiguration) =>
      handleValuesChanged(values, setIsSaveDisabled, config),
    [config, handleValuesChanged]
  );

  const handleSecondValuesChange = useCallback(
    (_: Partial<OutlookConfiguration>, values: OutlookConfiguration) =>
      handleValuesChanged(values, setIsSecondSaveDisabled, secondConfig),
    [handleValuesChanged, secondConfig]
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: config?.name ?? '',
        content: (
          <MS365ConfigurationDetails
            config={config}
            form={form}
            onValuesChange={handleFirstValuesChange}
          />
        ),
        drawerOpen: Boolean(config),
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText(`${intlPrefix}.delete_confirmation`)}
            handleDelete={() => onDelete(form.getFieldsValue())}
            handleDiscard={() => onDiscard(form.getFieldsValue())}
            handleSave={() => onSave(form.getFieldsValue())}
            isSaveDisable={isSaveDisabled}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(form.getFieldsValue()),
      }}
      secondDrawer={{
        title: secondConfig?.name ?? '',
        content: (
          <MS365ConfigurationDetails
            config={secondConfig}
            form={secondForm}
            onValuesChange={handleSecondValuesChange}
          />
        ),
        drawerOpen: Boolean(secondConfig),
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText(`${intlPrefix}.delete_confirmation`)}
            handleDelete={() => onDelete(secondForm.getFieldsValue())}
            handleDiscard={() => onDiscard(secondForm.getFieldsValue())}
            handleSave={() => onSave(secondForm.getFieldsValue())}
            isSaveDisable={isSecondSaveDisabled}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(secondForm.getFieldsValue()),
      }}
    />
  );
};

export default MS365ConfigurationDrawer;
